import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpErrorResponse, HttpEvent } from '@angular/common/http';
import { LoginService } from '@services/login/login.service';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { JwtPayload } from '@models/login/token-model.model';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  private loginService = inject(LoginService);
  private router = inject(Router);


  private readonly EXCLUDED_URLS = [
    '/api/Auth/Login',
    '/api/Auth/RefreshToken',
    '/api/EmpresaUsuario/RegisterEmpresaUsuario',
    '/api/Usuario/FirstRegister',
    '/api/Usuario/ConfirmarRegistroEmpresa',
  ];

  // Auditoría QA (2026-08-05) — Hallazgo medio #10: estado compartido para no disparar múltiples
  // refresh en paralelo cuando varios requests fallan con 401 al mismo tiempo (ej. varias
  // llamadas concurrentes al cargar una pantalla) — el primero refresca, el resto espera el
  // resultado y reintenta con el token nuevo.
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    if (this.shouldExcludeUrl(req.url)) {
      return next.handle(req);
    }

    const token = this.loginService.getToken();

    if (!token) {
      return next.handle(req);
    }

    return next.handle(this.addAuthHeaders(req, token)).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          return this.handle401(req, next);
        }
        return throwError(() => error);
      })
    );
  }

  // Auditoría QA (2026-08-05) — Hallazgo medio #10: antes, si el token local ya estaba vencido
  // (según el exp del JWT), el interceptor deslogueaba inmediatamente SIN intentar renovarlo —
  // por más que el backend ya guardaba un refresh token válido, nunca se le daba la oportunidad
  // de usarse. Ahora el flujo es reactivo: se manda el request igual, si el backend devuelve 401
  // (token vencido o inválido) se intenta refrescar UNA vez y reintentar; sólo si el refresh
  // también falla (o no hay refresh token guardado) se cierra la sesión.
  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.loginService.getRefreshToken();
      if (!refreshToken) {
        this.isRefreshing = false;
        this.logoutAndRedirect();
        return throwError(() => new HttpErrorResponse({ status: 401 }));
      }

      return this.loginService.refreshToken(refreshToken).pipe(
        switchMap(tokenModel => {
          this.isRefreshing = false;
          this.loginService.setToken(tokenModel);
          this.refreshTokenSubject.next(tokenModel.auth_Token);
          return next.handle(this.addAuthHeaders(req, tokenModel.auth_Token));
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.logoutAndRedirect();
          return throwError(() => err);
        })
      );
    }

    // Ya hay un refresh en curso (disparado por otro request) — esperar el token nuevo y
    // reintentar con ese, en vez de arrancar otro refresh en paralelo.
    return this.refreshTokenSubject.pipe(
      filter((token): token is string => token !== null),
      take(1),
      switchMap(token => next.handle(this.addAuthHeaders(req, token)))
    );
  }

  private logoutAndRedirect(): void {
    this.loginService.logout();
    this.router.navigate(['/login']);
  }

  private addAuthHeaders(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const idEmpresa = this.getCachedIdEmpresa(token);

    const isFormData = req.body instanceof FormData;
    const isBlob = req.body instanceof Blob;
    const expectsNonJsonResponse = req.responseType && req.responseType !== 'json';

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      ...(idEmpresa && { 'X-IdEmpresa': idEmpresa })
    };

    if (!isFormData && !isBlob && !expectsNonJsonResponse) {
      headers['Content-Type'] = 'application/json';
      headers['Accept'] = 'application/json';
    }

    return req.clone({ setHeaders: headers });
  }

  private shouldExcludeUrl(url: string): boolean {
    const path = url.split('?')[0];
    return this.EXCLUDED_URLS.includes(path);
  }

  private getCachedIdEmpresa(token: string): string | null {
  // Primero intentar obtener del localStorage (prioridad)
  const cachedIdEmpresa = localStorage.getItem('IdEmpresa');
  if (cachedIdEmpresa) return cachedIdEmpresa;

  // Fallback: intentar del token (por compatibilidad)
  const idEmpresa = this.getIdEmpresaFromToken(token);
  return idEmpresa;
}

  private getIdEmpresaFromToken(token: string): string | null {
    try {
      const payload = this.decodeJwtPayload(token);
      return payload?.IdEmpresa || null;
    } catch (error) {
      console.error('Error al obtener IdEmpresa del token:', error);
      return null;
    }
  }

  private decodeJwtPayload(token: string): JwtPayload | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
          .join('')
      );
      return JSON.parse(jsonPayload) as JwtPayload;
    } catch (error) {
      console.error('Error al decodificar el token:', error);
      return null;
    }
  }
}
